import { useLanguage } from '../contexts/LanguageContext';

// Translation mappings for dynamic content
const TRANSLATION_MAPPINGS = {
  // Categories
  categories: {
    'Vozila': {
      en: 'Vehicles',
      fr: 'Véhicules', 
      es: 'Vehículos',
      it: 'Veicoli',
      ru: 'Транспорт',
      ar: 'مركبات',
      de: 'Fahrzeuge'
    },
    'Kompjuteri': {
      en: 'Computers',
      fr: 'Ordinateurs',
      es: 'Computadoras', 
      it: 'Computer',
      ru: 'Компьютеры',
      ar: 'حاسوب',
      de: 'Computer'
    },
    'Mobilni uređaji': {
      en: 'Mobile Devices',
      fr: 'Appareils Mobiles',
      es: 'Dispositivos Móviles',
      it: 'Dispositivi Mobili', 
      ru: 'Мобильные устройства',
      ar: 'الأجهزة المحمولة',
      de: 'Mobile Geräte'
    },
    'Tehnika': {
      en: 'Technology',
      fr: 'Technologie',
      es: 'Tecnología',
      it: 'Tecnologia',
      ru: 'Технология', 
      ar: 'تقنية',
      de: 'Technologie'
    },
    'Moj dom': {
      en: 'My Home',
      fr: 'Ma Maison',
      es: 'Mi Hogar',
      it: 'La Mia Casa',
      ru: 'Мой дом',
      ar: 'منزلي', 
      de: 'Mein Zuhause'
    },
    'Ljepota i zdravlje': {
      en: 'Beauty & Health',
      fr: 'Beauté et Santé',
      es: 'Belleza y Salud',
      it: 'Bellezza e Salute',
      ru: 'Красота и здоровье',
      ar: 'الجمال والصحة',
      de: 'Schönheit & Gesundheit'
    }
  },

  // Product conditions
  conditions: {
    'Novo': {
      en: 'New',
      fr: 'Nouveau',
      es: 'Nuevo',
      it: 'Nuovo',
      ru: 'Новый',
      ar: 'جديد',
      de: 'Neu'
    },
    'Polovno': {
      en: 'Used',
      fr: 'Utilisé',
      es: 'Usado',
      it: 'Usato',
      ru: 'Б/у',
      ar: 'مستعمل',
      de: 'Gebraucht'
    },
    'Oštećeno': {
      en: 'Damaged',
      fr: 'Endommagé',
      es: 'Dañado',
      it: 'Danneggiato',
      ru: 'Поврежденный',
      ar: 'تالف',
      de: 'Beschädigt'
    }
  },

  // Locations/Regions
  locations: {
    'Banjalučka': {
      en: 'Banja Luka Region',
      fr: 'Région de Banja Luka',
      es: 'Región de Banja Luka',
      it: 'Regione di Banja Luka',
      ru: 'Регион Баня-Лука',
      ar: 'منطقة بانيا لوكا',
      de: 'Region Banja Luka'
    },
    'Sarajevo': {
      en: 'Sarajevo',
      fr: 'Sarajevo',
      es: 'Sarajevo',
      it: 'Sarajevo',
      ru: 'Сараево',
      ar: 'سراييفو',
      de: 'Sarajevo'
    },
    // Add more locations as needed
  },

  // Common UI elements
  ui: {
    'Promovisano': {
      en: 'Promoted',
      fr: 'Promu',
      es: 'Promocionado',
      it: 'Promosso',
      ru: 'Продвигается',
      ar: 'مروج',
      de: 'Beworben'
    },
    'Nepoznato': {
      en: 'Unknown',
      fr: 'Inconnu',
      es: 'Desconocido',
      it: 'Sconosciuto',
      ru: 'Неизвестно',
      ar: 'غير معروف',
      de: 'Unbekannt'
    },
    'Nema rezultata': {
      en: 'No results',
      fr: 'Aucun résultat',
      es: 'Sin resultados',
      it: 'Nessun risultato',
      ru: 'Нет результатов',
      ar: 'لا توجد نتائج',
      de: 'Keine Ergebnisse'
    },
    'Učitaj još': {
      en: 'Load more',
      fr: 'Charger plus',
      es: 'Cargar más',
      it: 'Carica altro',
      ru: 'Загрузить еще',
      ar: 'تحميل المزيد',
      de: 'Mehr laden'
    },
    'Dodajte novi proizvod': {
      en: 'Add new product',
      fr: 'Ajouter un nouveau produit',
      es: 'Agregar nuevo producto',
      it: 'Aggiungi nuovo prodotto',
      ru: 'Добавить новый товар',
      ar: 'إضافة منتج جديد',
      de: 'Neues Produkt hinzufügen'
    },
    'Popularne Radnje': {
      en: 'Popular Shops',
      fr: 'Boutiques Populaires',
      es: 'Tiendas Populares',
      it: 'Negozi Popolari',
      ru: 'Популярные магазины',
      ar: 'المتاجر الشعبية',
      de: 'Beliebte Geschäfte'
    }
  }
};

/**
 * Translates a single text value based on current language
 * @param {string} text - Original text to translate
 * @param {string} category - Category of translation (categories, conditions, etc.)
 * @param {string} currentLanguage - Current language code
 * @returns {string} Translated text or original if no translation found
 */
export const translateText = (text, category, currentLanguage = 'en') => {
  if (!text || !category || !TRANSLATION_MAPPINGS[category]) {
    return text;
  }

  const translations = TRANSLATION_MAPPINGS[category][text];
  if (translations && translations[currentLanguage]) {
    return translations[currentLanguage];
  }

  return text; // Return original if no translation found
};

/**
 * Translates an object's properties based on translation mappings
 * @param {Object} obj - Object to translate
 * @param {Object} fieldMappings - Mapping of field names to translation categories
 * @param {string} currentLanguage - Current language code
 * @returns {Object} Translated object
 */
export const translateObject = (obj, fieldMappings, currentLanguage = 'en') => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const translatedObj = { ...obj };

  Object.keys(fieldMappings).forEach(field => {
    if (translatedObj[field]) {
      const category = fieldMappings[field];
      translatedObj[field] = translateText(translatedObj[field], category, currentLanguage);
    }
  });

  return translatedObj;
};

/**
 * Translates an array of objects
 * @param {Array} array - Array of objects to translate
 * @param {Object} fieldMappings - Mapping of field names to translation categories
 * @param {string} currentLanguage - Current language code
 * @returns {Array} Array of translated objects
 */
export const translateArray = (array, fieldMappings, currentLanguage = 'en') => {
  if (!Array.isArray(array)) {
    return array;
  }

  return array.map(item => translateObject(item, fieldMappings, currentLanguage));
};

/**
 * Translates product data specifically
 * @param {Object|Array} data - Product data to translate
 * @param {string} currentLanguage - Current language code
 * @returns {Object|Array} Translated product data
 */
export const translateProductData = (data, currentLanguage = 'en') => {
  const productFieldMappings = {
    category: 'categories',
    condition: 'conditions',
    location: 'locations'
  };

  if (Array.isArray(data)) {
    return translateArray(data, productFieldMappings, currentLanguage);
  }

  return translateObject(data, productFieldMappings, currentLanguage);
};

/**
 * Translates category data specifically
 * @param {Object|Array} data - Category data to translate
 * @param {string} currentLanguage - Current language code
 * @returns {Object|Array} Translated category data
 */
export const translateCategoryData = (data, currentLanguage = 'en') => {
  const categoryFieldMappings = {
    name: 'categories',
    title: 'categories'
  };

  if (Array.isArray(data)) {
    return translateArray(data, categoryFieldMappings, currentLanguage);
  }

  return translateObject(data, categoryFieldMappings, currentLanguage);
};

/**
 * Translates UI text elements
 * @param {string} text - UI text to translate
 * @param {string} currentLanguage - Current language code
 * @returns {string} Translated UI text
 */
export const translateUI = (text, currentLanguage = 'en') => {
  return translateText(text, 'ui', currentLanguage);
};

/**
 * Hook for using translation functions with current language context
 * @returns {Object} Translation functions with current language applied
 */
export const useDataTranslation = () => {
  const { currentLanguage } = useLanguage();

  return {
    translateText: (text, category) => translateText(text, category, currentLanguage),
    translateObject: (obj, fieldMappings) => translateObject(obj, fieldMappings, currentLanguage),
    translateArray: (array, fieldMappings) => translateArray(array, fieldMappings, currentLanguage),
    translateProductData: (data) => translateProductData(data, currentLanguage),
    translateCategoryData: (data) => translateCategoryData(data, currentLanguage),
    translateUI: (text) => translateUI(text, currentLanguage),
    currentLanguage
  };
};

/**
 * Adds language parameter to API URLs
 * @param {string} url - Base API URL
 * @param {string} currentLanguage - Current language code
 * @returns {string} URL with language parameter
 */
export const addLanguageToAPI = (url, currentLanguage = 'en') => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}lang=${currentLanguage}`;
};

export default {
  translateText,
  translateObject,
  translateArray,
  translateProductData,
  translateCategoryData,
  translateUI,
  useDataTranslation,
  addLanguageToAPI,
  TRANSLATION_MAPPINGS
};
